import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  MessageSquare,
  Send,
  HelpCircle,
  GitBranch,
  Clock,
  Globe,
  StopCircle,
  Plus,
  Save,
  Play,
  Trash2,
  ArrowLeft,
  FileText,
  Sparkles,
  X,
  Webhook,
} from "lucide-react";
import {
  createFlow,
  updateFlow,
  getFlowById,
  generateFlowWithAI,
} from "../apis/flowAPI";
import TriggerNode from "../components/FlowNodes/TriggerNode";
import SendMessageNode from "../components/FlowNodes/SendMessageNode";
import AskQuestionNode from "../components/FlowNodes/AskQuestionNode";
import ConditionNode from "../components/FlowNodes/ConditionNode";
import DelayNode from "../components/FlowNodes/DelayNode";
import EndNode from "../components/FlowNodes/EndNode";
import WebhookNode from "../components/FlowNodes/WebhookNode";

// Register custom node types
const nodeTypes = {
  trigger: TriggerNode,
  sendMessage: SendMessageNode,
  askQuestion: AskQuestionNode,
  condition: ConditionNode,
  delay: DelayNode,
  end: EndNode,
  webhook: WebhookNode,
};

const FlowBuilder = () => {
  const { flowId: urlFlowId } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowName, setFlowName] = useState("");
  const [flowDescription, setFlowDescription] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodePanel, setShowNodePanel] = useState(true);
  const [flowId, setFlowId] = useState(urlFlowId || null);
  const [loading, setLoading] = useState(!!urlFlowId);
  const [saving, setSaving] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiDescription, setAiDescription] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);

  const onConnect = useCallback(
    (params) => {
      let conditionType = "always";
      let conditionValue = "";

      if (params.sourceHandle) {
        if (params.sourceHandle === "true") {
          conditionType = "true";
        } else if (params.sourceHandle === "false") {
          conditionType = "false";
        }
      }

      const newEdge = {
        ...params,
        markerEnd: { type: MarkerType.ArrowClosed },
        data: {
          conditionType,
          conditionValue,
        },
      };

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const currentSelectedNode = useMemo(() => {
    if (!selectedNode) return null;
    return nodes.find((n) => n.id === selectedNode.id) || selectedNode;
  }, [nodes, selectedNode]);

  useEffect(() => {
    const loadFlow = async () => {
      if (!urlFlowId) return;

      try {
        setLoading(true);
        const token = localStorage.getItem("userAccessToken");
        const response = await getFlowById(token, urlFlowId);
        const flow = response.data.flow;

        setFlowName(flow.name || "Untitled Flow");
        setFlowDescription(flow.description || "");
        setFlowId(flow.id);

        const reactFlowNodes = flow.nodes.map((node) => ({
          id: node.node_id,
          type: node.node_type,
          position: { x: node.position_x, y: node.position_y },
          data: node.config,
        }));
        setNodes(reactFlowNodes);

        const reactFlowEdges = flow.connections.map((conn) => {
          const edge = {
            id: `e${conn.source_node_id}-${conn.target_node_id}`,
            source: conn.source_node_id,
            target: conn.target_node_id,
            markerEnd: { type: MarkerType.ArrowClosed },
            data: {
              conditionType: conn.condition_type || "always",
              conditionValue: conn.condition_value || "",
            },
          };

          if (
            conn.condition_type === "true" ||
            conn.condition_type === "false"
          ) {
            edge.sourceHandle = conn.condition_type;
          }

          return edge;
        });
        setEdges(reactFlowEdges);
      } catch (error) {
        console.error("Error loading flow:", error);
        alert("Failed to load flow");
        navigate("/flow-list");
      } finally {
        setLoading(false);
      }
    };

    loadFlow();
  }, [urlFlowId, navigate, setNodes, setEdges]);

  const addNode = (type) => {
    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: getDefaultNodeData(type),
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const getDefaultNodeData = (type) => {
    switch (type) {
      case "trigger":
        return { label: "Flow Trigger", keywords: [], triggerType: "keyword" };
      case "sendMessage":
        return { label: "Send Message", message: "", templateId: null };
      case "askQuestion":
        return { label: "Ask Question", question: "", variableName: "" };
      case "condition":
        return { label: "Condition", conditions: [] };
      case "delay":
        return { label: "Delay", duration: 5, unit: "seconds" };
      case "webhook":
        return {
          label: "Webhook",
          webhookUrl: "",
          method: "POST",
          headers: "{}",
          requestBody: "",
          inputVariable: "",
          responseVariable: "webhook_response",
          responseMessage: "Data fetched successfully",
        };
      case "end":
        return { label: "End Flow" };
      default:
        return { label: type };
    }
  };

  const handleSaveFlow = async () => {
    if (!flowName.trim()) {
      alert("Please enter a flow name");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("userAccessToken");
      const flowData = {
        name: flowName.trim(),
        description: flowDescription.trim(),
        nodes: nodes.map((n) => ({
          nodeId: n.id,
          type: n.type,
          positionX: n.position.x,
          positionY: n.position.y,
          config: n.data,
        })),
        connections: edges.map((e) => ({
          sourceNodeId: e.source,
          targetNodeId: e.target,
          conditionType:
            e.data?.conditionType ||
            (e.sourceHandle === "true"
              ? "true"
              : e.sourceHandle === "false"
              ? "false"
              : "always"),
          conditionValue: e.data?.conditionValue || "",
        })),
        isActive: true,
      };

      let result;
      if (flowId) {
        result = await updateFlow(token, flowId, flowData);
        alert("Flow updated successfully!");
      } else {
        result = await createFlow(token, flowData);
        const newFlowId = result.data.flowId;
        setFlowId(newFlowId);
        navigate(`/flows/builder/${newFlowId}`, { replace: true });
        alert("Flow created successfully!");
      }
    } catch (error) {
      console.error("Error saving flow:", error);
      const errorResponse = error.response?.data;
      // Don't show error for subscription issues - SubscriptionGate handles it
      if (!errorResponse?.requiresSubscription) {
        alert(errorResponse?.error || "Failed to save flow");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNode = () => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      setEdges((eds) =>
        eds.filter(
          (e) => e.source !== selectedNode.id && e.target !== selectedNode.id
        )
      );
      setSelectedNode(null);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!aiDescription.trim()) {
      alert("Please enter a description of your flow");
      return;
    }

    try {
      setAiGenerating(true);
      const token = localStorage.getItem("userAccessToken");
      const response = await generateFlowWithAI(
        token,
        aiDescription,
        flowName || "AI Generated Flow"
      );

      const generatedFlow = response.data.flow;

      const reactFlowNodes = generatedFlow.nodes.map((node) => ({
        id: node.id,
        type: node.type,
        position: node.position || {
          x: Math.random() * 400,
          y: Math.random() * 400,
        },
        data: node.data || getDefaultNodeData(node.type),
      }));

      const reactFlowEdges = generatedFlow.connections.map((conn, idx) => {
        const edge = {
          id: `e${conn.source}-${conn.target}-${idx}`,
          source: conn.source,
          target: conn.target,
          markerEnd: { type: MarkerType.ArrowClosed },
          data: {
            conditionType: conn.conditionType || "always",
            conditionValue: conn.conditionValue || "",
          },
        };

        if (conn.conditionType === "true" || conn.conditionType === "false") {
          edge.sourceHandle = conn.conditionType;
        }

        return edge;
      });

      setNodes(reactFlowNodes);
      setEdges(reactFlowEdges);

      if (response.data.name && !flowName) {
        setFlowName(response.data.name);
      }

      setShowAIPanel(false);
      setAiDescription("");

      alert("Flow generated successfully! You can now edit and save it.");
    } catch (error) {
      console.error("Error generating flow with AI:", error);
      const errorResponse = error.response?.data;
      // Don't show error for subscription issues - SubscriptionGate handles it
      if (!errorResponse?.requiresSubscription) {
        alert(
          errorResponse?.error ||
            "Failed to generate flow. Please try again."
        );
      }
    } finally {
      setAiGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#fff]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-700 text-sm font-semibold">Loading flow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#fff]">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm mx-6 mt-4 rounded-2xl">
        <div className="px-4 py-3">
          {/* First Row */}
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => navigate("/flow-list")}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0 border border-gray-200"
              title="Back to flows"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>

            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                className="text-sm font-bold w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-400 focus:outline-none transition-all bg-white"
                placeholder="Flow name *"
                autoFocus={!flowId}
              />
            </div>

            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={flowDescription}
                onChange={(e) => setFlowDescription(e.target.value)}
                className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-400 focus:outline-none transition-all bg-white"
                placeholder="Description (optional)"
              />
            </div>
          </div>

          {/* Second Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-xl border border-blue-200">
                  <MessageSquare size={14} className="text-blue-600" />
                  <span className="font-bold text-blue-900">{nodes.length}</span>
                  <span className="text-gray-600 font-medium">nodes</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-xl border border-emerald-200">
                  <GitBranch size={14} className="text-emerald-600" />
                  <span className="font-bold text-emerald-900">{edges.length}</span>
                  <span className="text-gray-600 font-medium">connections</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/flow-list")}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-xl transition-colors border border-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFlow}
                disabled={saving || !flowName.trim()}
                className="flex items-center gap-2 text-gray-900 px-5 py-2.5 text-sm rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed font-bold border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <Save size={18} />
                {saving ? "Saving..." : flowId ? "Save Changes" : "Save Flow"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden gap-4 px-4 pb-4 mt-4">
        {/* AI Generation Panel */}
        {showAIPanel && (
          <div className="w-80 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-50 rounded-xl border border-purple-200">
                  <Sparkles className="text-purple-600" size={18} />
                </div>
                <h3 className="font-bold text-sm text-gray-900">AI Flow Generator</h3>
              </div>
              <button
                onClick={() => {
                  setShowAIPanel(false);
                  setAiDescription("");
                }}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="mb-4">
                <label className="block text-xs font-bold text-gray-700 mb-2">
                  Describe your flow
                </label>
                <textarea
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  className="w-full px-4 py-3 text-sm font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-400 focus:outline-none resize-none"
                  rows={8}
                  placeholder="Example: When user sends 'hi', send a welcome message, wait 5 seconds, then ask their name and save it as 'user_name'. If they say 'yes', send a confirmation message, otherwise end the flow."
                />
                <p className="text-xs text-gray-500 mt-2 font-medium">
                  Describe your chatbot flow in natural language. The AI will create the flow structure automatically.
                </p>
              </div>
              <div className="space-y-2">
                <button
                  onClick={handleGenerateWithAI}
                  disabled={aiGenerating || !aiDescription.trim()}
                  className="w-full bg-purple-600 text-white px-4 py-3 text-sm rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold flex items-center justify-center gap-2 shadow-sm"
                >
                  {aiGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Generate Flow
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAIPanel(false);
                    setAiDescription("");
                  }}
                  className="w-full bg-white text-gray-700 px-4 py-2.5 text-sm rounded-xl hover:bg-gray-50 transition-colors border border-gray-300 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Left Sidebar - Node Panel */}
        {showNodePanel && (
          <div className="w-64 bg-white border border-gray-200 rounded-2xl p-4 overflow-y-auto shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm text-gray-900">Add Nodes</h3>
              <button
                onClick={() => setShowAIPanel(!showAIPanel)}
                className="p-2 hover:bg-purple-50 rounded-xl transition-colors border border-purple-200"
                title="Generate flow with AI"
              >
                <Sparkles size={18} className="text-purple-600" />
              </button>
            </div>
            <div className="space-y-2">
              <NodeButton
                icon={<MessageSquare size={20} />}
                label="Trigger"
                onClick={() => addNode("trigger")}
              />
              <NodeButton
                icon={<Send size={20} />}
                label="Send Message"
                onClick={() => addNode("sendMessage")}
              />
              <NodeButton
                icon={<HelpCircle size={20} />}
                label="Ask Question"
                onClick={() => addNode("askQuestion")}
              />
              <NodeButton
                icon={<GitBranch size={20} />}
                label="Condition"
                onClick={() => addNode("condition")}
              />
              <NodeButton
                icon={<Clock size={20} />}
                label="Delay"
                onClick={() => addNode("delay")}
              />
              <NodeButton
                icon={<Webhook size={20} />}
                label="Webhook"
                onClick={() => addNode("webhook")}
              />
              <NodeButton
                icon={<StopCircle size={20} />}
                label="End Flow"
                onClick={() => addNode("end")}
              />
            </div>
          </div>
        )}

        {/* Main Canvas */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            nodesConnectable={true}
            nodesDraggable={true}
            edgesUpdatable={true}
            connectionLineStyle={{ stroke: "#3b82f6", strokeWidth: 2 }}
            connectionLineType="smoothstep"
            defaultEdgeOptions={{
              type: "smoothstep",
              animated: false,
              style: { stroke: "#3b82f6", strokeWidth: 2 },
            }}
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        {/* Right Sidebar - Node Properties */}
        {currentSelectedNode && (
          <div className="w-80 bg-white border border-gray-200 rounded-2xl p-4 overflow-y-auto shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-sm text-gray-900">Node Properties</h3>
              <button
                onClick={handleDeleteNode}
                className="text-red-600 hover:bg-red-50 p-2 rounded-xl transition-colors border border-red-200"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <NodePropertyPanel node={currentSelectedNode} setNodes={setNodes} />
          </div>
        )}
      </div>
    </div>
  );
};

// Node Button Component
const NodeButton = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm hover:shadow"
  >
    <div className="text-blue-600">{icon}</div>
    <span className="font-bold text-sm text-gray-900">{label}</span>
  </button>
);

// Node Property Panel
const NodePropertyPanel = ({ node, setNodes }) => {
  const updateNodeData = (key, value) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id ? { ...n, data: { ...n.data, [key]: value } } : n
      )
    );
  };

  // Trigger Node
  if (node.type === "trigger") {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">Trigger Type</label>
          <select
            value={node.data.triggerType || "keyword"}
            onChange={(e) => updateNodeData("triggerType", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-gray-400 focus:outline-none"
          >
            <option value="keyword">Keyword</option>
            <option value="welcome">Welcome Message</option>
            <option value="menu">Main Menu</option>
            <option value="default">Default (Fallback)</option>
          </select>
        </div>
        {node.data.triggerType === "keyword" && (
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-2">
              Keywords (comma separated)
            </label>
            <input
              key={`keywords-${node.id}`}
              type="text"
              value={node.data.keywords?.join(", ") || ""}
              onChange={(e) =>
                updateNodeData(
                  "keywords",
                  e.target.value.split(",").map((k) => k.trim())
                )
              }
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none"
              placeholder="hi, hello, start"
            />
          </div>
        )}
      </div>
    );
  }

  // Send Message Node
  if (node.type === "sendMessage") {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">Message Text</label>
          <textarea
            key={`message-${node.id}`}
            value={node.data.message || ""}
            onChange={(e) => updateNodeData("message", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none resize-none"
            rows={4}
            placeholder="Enter message to send..."
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Use Template?
          </label>
          <select
            value={node.data.useTemplate ? "yes" : "no"}
            onChange={(e) =>
              updateNodeData("useTemplate", e.target.value === "yes")
            }
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-gray-400 focus:outline-none"
          >
            <option value="no">No, send text message</option>
            <option value="yes">Yes, use template</option>
          </select>
        </div>
      </div>
    );
  }

  // Ask Question Node
  if (node.type === "askQuestion") {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">Question</label>
          <textarea
            key={`question-${node.id}`}
            value={node.data.question || ""}
            onChange={(e) => updateNodeData("question", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none resize-none"
            rows={3}
            placeholder="What question to ask?"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Save Response As
          </label>
          <input
            key={`variableName-${node.id}`}
            type="text"
            value={node.data.variableName || ""}
            onChange={(e) => updateNodeData("variableName", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none"
            placeholder="variable_name"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Expected Answer Type
          </label>
          <select
            value={node.data.answerType || "text"}
            onChange={(e) => updateNodeData("answerType", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-gray-400 focus:outline-none"
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="email">Email</option>
            <option value="phone">Phone Number</option>
          </select>
        </div>
      </div>
    );
  }

  // Condition Node
  if (node.type === "condition") {
    const conditions = node.data.conditions || [];

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">Conditions</label>
          {conditions.map((cond, index) => (
            <div
              key={`condition-${node.id}-${index}`}
              className="border border-gray-200 rounded-xl p-3 mb-3 bg-gray-50"
            >
              <div className="mb-2">
                <label className="text-xs text-gray-600 font-semibold">Variable</label>
                <input
                  key={`condition-var-${node.id}-${index}`}
                  type="text"
                  value={cond.variable || ""}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].variable = e.target.value;
                    updateNodeData("conditions", newConditions);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-medium mt-1"
                  placeholder="variable_name"
                />
              </div>
              <div className="mb-2">
                <label className="text-xs text-gray-600 font-semibold">Operator</label>
                <select
                  key={`condition-op-${node.id}-${index}`}
                  value={cond.operator || "equals"}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].operator = e.target.value;
                    updateNodeData("conditions", newConditions);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-semibold mt-1"
                >
                  <option value="equals">Equals</option>
                  <option value="contains">Contains</option>
                  <option value="greater">Greater than</option>
                  <option value="less">Less than</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="text-xs text-gray-600 font-semibold">Value</label>
                <input
                  key={`condition-val-${node.id}-${index}`}
                  type="text"
                  value={cond.value || ""}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].value = e.target.value;
                    updateNodeData("conditions", newConditions);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-medium mt-1"
                  placeholder="comparison value"
                />
              </div>
              <button
                onClick={() => {
                  const newConditions = conditions.filter(
                    (_, i) => i !== index
                  );
                  updateNodeData("conditions", newConditions);
                }}
                className="text-red-600 text-xs font-bold hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const newConditions = [
                ...conditions,
                { variable: "", operator: "equals", value: "" },
              ];
              updateNodeData("conditions", newConditions);
            }}
            className="text-blue-600 text-sm font-bold hover:underline"
          >
            + Add Condition
          </button>
        </div>
      </div>
    );
  }

  // Delay Node
  if (node.type === "delay") {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">Duration</label>
          <input
            type="number"
            value={node.data.duration || 5}
            onChange={(e) =>
              updateNodeData("duration", parseInt(e.target.value))
            }
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-gray-400 focus:outline-none"
            min="1"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">Unit</label>
          <select
            value={node.data.unit || "seconds"}
            onChange={(e) => updateNodeData("unit", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-gray-400 focus:outline-none"
          >
            <option value="seconds">Seconds</option>
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
          </select>
        </div>
      </div>
    );
  }

  // Webhook Node
  if (node.type === "webhook") {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Webhook URL *
          </label>
          <input
            key={`webhookUrl-${node.id}`}
            type="url"
            value={node.data.webhookUrl || ""}
            onChange={(e) => updateNodeData("webhookUrl", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none"
            placeholder="https://your-server.com/api/webhook"
          />
          <p className="text-xs text-gray-500 mt-1 font-medium">
            Your webhook endpoint URL
          </p>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">HTTP Method</label>
          <select
            value={node.data.method || "POST"}
            onChange={(e) => updateNodeData("method", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-gray-400 focus:outline-none"
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Input Variable
          </label>
          <input
            key={`inputVariable-${node.id}`}
            type="text"
            value={node.data.inputVariable || ""}
            onChange={(e) => updateNodeData("inputVariable", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none"
            placeholder="item, order_id, ticket_id, etc."
          />
          <p className="text-xs text-gray-500 mt-1 font-medium">
            Variable name from previous question
          </p>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Request Headers (JSON)
          </label>
          <textarea
            key={`headers-${node.id}`}
            value={node.data.headers || "{}"}
            onChange={(e) => updateNodeData("headers", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 font-mono text-xs font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none resize-none"
            rows={2}
            placeholder='{"Authorization": "Bearer token"}'
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Request Body (JSON)
          </label>
          <textarea
            key={`requestBody-${node.id}`}
            value={node.data.requestBody || ""}
            onChange={(e) => updateNodeData("requestBody", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 font-mono text-xs font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none resize-none"
            rows={4}
            placeholder='{"order_id": "{inputVariable}"}'
          />
          <p className="text-xs text-gray-500 mt-1 font-medium">
            Use {"{inputVariable}"} to insert the input variable
          </p>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Response Variable Name
          </label>
          <input
            key={`responseVariable-${node.id}`}
            type="text"
            value={node.data.responseVariable || "webhook_response"}
            onChange={(e) => updateNodeData("responseVariable", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none"
            placeholder="webhook_response"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Success Message Template
          </label>
          <textarea
            key={`responseMessage-${node.id}`}
            value={node.data.responseMessage || ""}
            onChange={(e) => updateNodeData("responseMessage", e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-gray-400 focus:outline-none resize-none"
            rows={3}
            placeholder="Order {order_id} status: {status}"
          />
          <p className="text-xs text-gray-500 mt-1 font-medium">
            Use {"{fieldName}"} to access response data fields
          </p>
        </div>
      </div>
    );
  }

  // End Node
  if (node.type === "end") {
    return (
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-sm text-gray-600 font-medium">
          This node ends the conversation flow. No configuration needed.
        </p>
      </div>
    );
  }

  return (
    <div className="text-gray-500 text-sm font-medium p-4 bg-gray-50 rounded-xl border border-gray-200">
      Select a node to edit its properties
    </div>
  );
};

export default FlowBuilder;
